import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { IUserModel } from "../../../../common/infrastructure/hooks/useAuthentication";
import { useAppContext } from "../../../../AppContext";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { FeatureFlagKey, UserRole } from "../../../../common/enums";
import { useFeatureFlag } from "../../../../common/infrastructure/hooks/useFeatureFlag";
import {
  canSeeNotificationsTab,
  isStaffRole,
} from "../../../../common/infrastructure/utilities/pushUtility";
import { ProfileTab } from "../models/profileTab";
import { canSeePlannyAccessLog } from "../../../../common/utilities/plannyAccessUtility";

const VALID_TABS: ProfileTab[] = [
  "personal",
  "password",
  "stripe",
  "integrations",
  "business",
  "mcp",
  "notifications",
  "plannyAccess",
];

export const useMyProfile = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAppContext();
  const [searchParams] = useSearchParams();
  const [isEditingPersonalData, setIsEditingPersonalData] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>(() => {
    const fromQuery = searchParams.get("tab") as ProfileTab | null;
    return fromQuery && VALID_TABS.includes(fromQuery) ? fromQuery : "personal";
  });

  const { data: profile, isLoading } = useApiQuery<IUserModel>({
    queryKey: [endpointsMetadata.authentication.getUserProfile],
    endpoint: endpointsMetadata.authentication.getUserProfile,
    globalIsLoading: false,
  });

  const invalidateProfile = () => {
    queryClient.invalidateQueries({
      queryKey: [endpointsMetadata.authentication.getUserProfile],
    });
  };

  const currentRole = getCurrentUserRole(currentUser);
  const isTenant = currentRole === UserRole.Tenant;
  // A profile a global admin opened from the admin panel (see ImpersonationBanner).
  const isOpenedProfile = !!currentUser?.impersonatedBy;
  // Managers and owners (and above). Mirrors the backend gate on McpController
  // ([MinimumRoleAuthorize(Employee_Manager)]). Not in an opened profile: a token would act as
  // the owner long after the admin's hour-long session ends, so the API refuses to create one.
  const canManageMcp =
    currentRole !== undefined && currentRole >= UserRole.Employee_Manager && !isOpenedProfile;
  // The studio's owner and its managers see what global admins did in their studios.
  const canSeePlannyAccess = canSeePlannyAccessLog(currentRole);
  // Device (push) notifications — every role, behind the enable-app-notifications flag. Not in a
  // profile a global admin opened from the admin panel: this browser's subscription is the admin's.
  const appNotificationsEnabled = useFeatureFlag(FeatureFlagKey.EnableAppNotifications, false);
  const canUseDeviceNotifications = appNotificationsEnabled && !isOpenedProfile;
  // Staff also get the "new reservations" email there, so they see the tab even without the flag.
  const isStaff = isStaffRole(currentRole);
  const canManageNotifications = canSeeNotificationsTab({
    role: currentRole,
    appNotificationsEnabled: canUseDeviceNotifications,
  });

  useEffect(() => {
    const fromQuery = searchParams.get("tab") as ProfileTab | null;
    if (fromQuery && VALID_TABS.includes(fromQuery)) {
      setActiveTab(fromQuery);
    }
  }, [searchParams]);

  useEffect(() => {
    if (
      !isTenant &&
      (activeTab === "stripe" || activeTab === "integrations" || activeTab === "business")
    ) {
      setActiveTab("personal");
    }
    if (!canSeePlannyAccess && activeTab === "plannyAccess") {
      setActiveTab("personal");
    }
    if (!canManageMcp && activeTab === "mcp") {
      setActiveTab("personal");
    }
    if (!canManageNotifications && activeTab === "notifications") {
      setActiveTab("personal");
    }
  }, [isTenant, canManageMcp, canManageNotifications, canSeePlannyAccess, activeTab]);

  return {
    profile,
    isLoading,
    isEditingPersonalData,
    setIsEditingPersonalData,
    invalidateProfile,
    isTenant,
    canManageMcp,
    isOpenedProfile,
    canManageNotifications,
    canUseDeviceNotifications,
    isStaff,
    canSeePlannyAccess,
    activeTab,
    setActiveTab,
  };
};
